import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { User, UserManager, UserManagerSettings, UserProfile } from 'oidc-client-ts';

import type { ApiAuthenticationProviders, ApiUser, AppSpec } from '@izimi/api';
import { Event, EventRegister } from '@izimi/utils';

import type { AuthState } from './authReducer';
import { authReducer, getInitialState } from './authReducer';
import {
  createUserManager,
  getDataFromState,
  getDefaultRedirectUrl,
  getStoredAuthenticationProviderName,
  hasAuthParams,
  normalizeErrorFn,
  setStoredAuthenticationProviderName,
  unsupportedEnvironment,
} from './utils';

interface AuthContextProps
  extends
    AuthState,
    Pick<
      UserManager,
      | 'settings'
      | 'events'
      | 'signinPopup'
      | 'signinSilent'
      | 'signinRedirect'
      | 'signoutRedirect'
      | 'signoutPopup'
    > {
  authenticationProviders?: ApiAuthenticationProviders;
  setUser: (user: ApiUser) => Promise<void>;
  onLogin: (options?: { authenticationProvider?: ApiAuthenticationProviders[number] }) => void;
}

const AuthContext = createContext<AuthContextProps | null>(null);
const LANGUAGE_QUERY_PARAMETER_KEY = 'lng';
const STATE_LANGUAGE_KEY = 'lang';

interface AuthManagerProps {
  authenticationProviders: ApiAuthenticationProviders | undefined;
  apiUrl: string;
  children?: ReactNode;
  postLogoutRedirectUrl?: string;
  loadUserInfo?: boolean;
  appSpec?: AppSpec;
  onSigninCallback?: (user: User | void, language: string | null) => Promise<void> | void;
}

const navigatorKeys = [
  'signinPopup',
  'signinSilent',
  'signinRedirect',
  'signoutPopup',
  'signoutRedirect',
] as const;

const storedAuthenticationProviderName = getStoredAuthenticationProviderName();

export const logout = () => {
  EventRegister.emit(Event.Logout);
};

export const AuthManager = ({
  children,
  onSigninCallback,
  authenticationProviders,
  apiUrl,
  postLogoutRedirectUrl,
  loadUserInfo,
  appSpec,
}: AuthManagerProps) => {
  const [userManager, setUserManager] = useState<UserManager>();
  const [state, dispatch] = useReducer(authReducer, getInitialState());

  const authenticationProvider = useMemo(
    () =>
      authenticationProviders?.find(
        (provider) => provider.providerName === storedAuthenticationProviderName,
      ) ?? authenticationProviders?.[0],
    [authenticationProviders],
  );

  const getSignoutParameters = useCallback<
    (params?: { withLogoutQueryParameter?: boolean }) => Partial<UserManagerSettings>
  >(
    ({ withLogoutQueryParameter } = { withLogoutQueryParameter: true }) => {
      const postLogoutRedirectUriKey = authenticationProvider?.logoutRedirectParam;
      const _postLogoutRedirectUrl = postLogoutRedirectUrl ?? getDefaultRedirectUrl({ apiUrl });

      return {
        post_logout_redirect_uri: postLogoutRedirectUriKey ? undefined : _postLogoutRedirectUrl,
        extraQueryParams: {
          ...(postLogoutRedirectUriKey && {
            [postLogoutRedirectUriKey]: _postLogoutRedirectUrl,
          }),
          ...(withLogoutQueryParameter && {
            logout: true,
          }),
        },
      };
    },
    [postLogoutRedirectUrl, apiUrl, authenticationProvider],
  );

  useEffect(() => {
    if (authenticationProvider && apiUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUserManager(createUserManager(authenticationProvider, apiUrl, loadUserInfo, appSpec));
    }
  }, [authenticationProvider, apiUrl, loadUserInfo, appSpec]);

  useEffect(() => {
    if (!userManager || !state.user?.access_token) {
      return undefined;
    }

    const eventId = EventRegister.addEventListener(Event.Logout, () => {
      // TODO: this should eventually be replaced by an actual id_token, not the access token
      // This line ensures the user correctly logs out
      userManager.signoutRedirect({
        id_token_hint: state.user?.access_token,
        ...getSignoutParameters({ withLogoutQueryParameter: Boolean(state.user?.expired) }),
      });
    });

    return () => {
      EventRegister.removeEventListener(eventId);
    };
  }, [userManager, state.user, getSignoutParameters]);

  const onLogin = useCallback(
    ({
      authenticationProvider,
    }: { authenticationProvider?: ApiAuthenticationProviders[number] } = {}) => {
      const params = new URLSearchParams(window.location.search);
      const languageQueryParameter = params.get(LANGUAGE_QUERY_PARAMETER_KEY);

      const state = JSON.stringify({
        ...(languageQueryParameter && { [STATE_LANGUAGE_KEY]: languageQueryParameter }),
      });

      if (authenticationProvider) {
        setUserManager(() => {
          const nextUserManager = createUserManager(authenticationProvider, apiUrl);
          setStoredAuthenticationProviderName(authenticationProvider.providerName);
          nextUserManager.signinRedirect({ state });
          return nextUserManager;
        });
      } else if (userManager) {
        userManager.signinRedirect({ state });
      }
    },
    [apiUrl, userManager],
  );

  const userManagerContext = useMemo(() => {
    if (!userManager) {
      return;
    }

    return {
      settings: userManager.settings,
      events: userManager.events,
      ...(Object.fromEntries(
        navigatorKeys.map((key) => [
          key,
          userManager[key]
            ? async (...args: never[]) => {
                try {
                  return await userManager[key](...args);
                } catch (error) {
                  dispatch({ type: 'ERROR', error });
                }
              }
            : unsupportedEnvironment(key),
        ]),
      ) as Pick<UserManager, (typeof navigatorKeys)[number]>),
    };
  }, [userManager]);

  const didInitialize = useRef(false);

  useEffect(() => {
    if (!userManager || didInitialize.current) {
      return;
    }
    didInitialize.current = true;
    (async () => {
      try {
        if (hasAuthParams()) {
          const user2 = await userManager.signinCallback();
          const language =
            typeof user2 === 'object' && user2.state
              ? getDataFromState({ state: user2.state, key: STATE_LANGUAGE_KEY })
              : null;

          onSigninCallback?.(user2, language);
        }

        const user = await userManager.getUser();
        dispatch({ type: 'INITIALISED', user });
      } catch (error) {
        dispatch({
          type: 'ERROR',
          error: normalizeErrorFn('unable to login')(error),
        });
      }
    })();
  }, [userManager, onSigninCallback, state.user]);

  useEffect(() => {
    if (!userManager) {
      return;
    }

    const handleUserLoaded = (user: User) => {
      dispatch({ type: 'USER_LOADED', user });
    };
    userManager.events.addUserLoaded(handleUserLoaded);
    const handleUserUnloaded = () => {
      dispatch({ type: 'USER_UNLOADED' });
    };
    userManager.events.addUserUnloaded(handleUserUnloaded);
    const handleSilentRenewError = (error: Error) => {
      dispatch({ type: 'ERROR', error });
    };
    userManager.events.addSilentRenewError(handleSilentRenewError);

    const handleAccessTokenExpiring = () => {
      // This handles the automatic logout 60s before the access token expires
      // We do this because we need to logout with the access token still valid
      // The logout parameter is added so backend knows it's an "auto logout"
      userManager?.signoutRedirect({
        id_token_hint: state.user?.access_token,
        ...getSignoutParameters(),
      });
      dispatch({ type: 'SESSION_EXPIRED' });
    };
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
    };
  }, [userManager, state.user]);

  const setUser = useCallback(
    async (user: ApiUser) => {
      const currentUser = await userManager?.getUser();
      if (currentUser) {
        currentUser.profile = user as unknown as UserProfile;
        await userManager?.storeUser(currentUser);
        dispatch({ type: 'USER_LOADED', user: currentUser });
      }
    },
    [userManager],
  );

  const value = useMemo(
    () => ({ ...state, ...userManagerContext, authenticationProviders, setUser, onLogin }),
    [state, userManagerContext, authenticationProviders, setUser, onLogin],
  );

  return <AuthContext.Provider value={value as AuthContextProps}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Wrap app with AuthManager');
  }
  return context;
};
